// Google OAuth authorization code flow with PKCE + refresh tokens.
// Session hard-capped at 7 days from the original grant.
//
// Storage split:
//   sessionStorage ds_token   — short-lived id_token blob (per-tab, gone on close)
//   localStorage   ds_refresh — refresh_token + 7-day anchor (survives tab close)
//
// On fresh tab open we re-hydrate sessionStorage from localStorage by
// exchanging the refresh_token for a new id_token — so the 7-day session
// actually works across tabs and browser restarts.
//
// clearSession() — wipes in-memory + sessionStorage only. Safe on transient
//   failures (refresh 5xx) so the refresh_token stays for the next attempt.
// signOut()      — wipes everything including the refresh_token.

import React, { createContext, useContext, useEffect, useReducer, useRef } from 'react';
import { portalConfig } from './portal-config.js';

let inFlightRefresh: Promise<void> | null = null;

const STATE_KEY = 'ds_oauth_state';
const VERIFIER_KEY = 'ds_code_verifier';
const TOKEN_KEY = 'ds_token';
const REFRESH_KEY = 'ds_refresh';
const SESSION_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const REFRESH_BUFFER_MS = 5 * 60 * 1000;

interface StoredSession {
  idToken: string;
  email: string;
  expiresAt: number;
  sessionStartedAt: number;
}

interface RefreshEntry {
  refreshToken: string;
  sessionStartedAt: number;
}

interface AuthState {
  idToken: string | null;
  email: string | null;
  isLoading: boolean;
  signIn: () => Promise<void>;
  signOut: () => void;
}

type Action =
  | { type: 'SET_SESSION'; session: StoredSession }
  | { type: 'CLEAR_SESSION' }
  | { type: 'LOADED' };

interface InternalState {
  idToken: string | null;
  email: string | null;
  isLoading: boolean;
}

function reducer(state: InternalState, action: Action): InternalState {
  switch (action.type) {
    case 'SET_SESSION':
      return { idToken: action.session.idToken, email: action.session.email, isLoading: false };
    case 'CLEAR_SESSION':
      return { idToken: null, email: null, isLoading: false };
    case 'LOADED':
      return { ...state, isLoading: false };
    default:
      return state;
  }
}

const AuthContext = createContext<AuthState>({
  idToken: null,
  email: null,
  isLoading: true,
  signIn: async () => {},
  signOut: () => {},
});

function parseIdToken(token: string): { email: string; exp: number } {
  const parts = token.split('.');
  if (parts.length < 2) throw new Error('bad id_token');
  let b64 = parts[1]!.replace(/-/g, '+').replace(/_/g, '/');
  while (b64.length % 4) b64 += '=';
  const payload = JSON.parse(atob(b64)) as { email: string; exp: number };
  return { email: payload.email, exp: payload.exp };
}

async function sha256Base64Url(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const hash = await crypto.subtle.digest('SHA-256', data);
  const bytes = new Uint8Array(hash);
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomString(len: number): string {
  const bytes = new Uint8Array(len);
  crypto.getRandomValues(bytes);
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '').slice(0, len);
}

function saveRefresh(entry: RefreshEntry): void {
  try { localStorage.setItem(REFRESH_KEY, JSON.stringify(entry)); } catch { /* quota/disabled */ }
}

function loadRefresh(): RefreshEntry | null {
  try {
    const raw = localStorage.getItem(REFRESH_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as RefreshEntry;
    if (typeof parsed?.refreshToken !== 'string' || typeof parsed?.sessionStartedAt !== 'number') return null;
    return parsed;
  } catch {
    return null;
  }
}

function clearRefresh(): void {
  try { localStorage.removeItem(REFRESH_KEY); } catch { /* disabled */ }
}

function cleanUrl(): void {
  history.replaceState(null, '', window.location.pathname);
}

function persistSession(session: StoredSession): void {
  sessionStorage.setItem(TOKEN_KEY, JSON.stringify(session));
}

async function exchangeCode(apiBase: string, code: string, codeVerifier: string) {
  const res = await fetch(`${apiBase}/auth/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ grant_type: 'authorization_code', code, code_verifier: codeVerifier }),
  });
  if (!res.ok) throw new Error('Token exchange failed');
  return (await res.json()) as { idToken: string; refreshToken?: string; expiresIn: number };
}

async function exchangeRefresh(apiBase: string, refreshToken: string) {
  const res = await fetch(`${apiBase}/auth/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ grant_type: 'refresh_token', refresh_token: refreshToken }),
  });
  if (!res.ok) throw new Error('Refresh failed');
  return (await res.json()) as { idToken: string; expiresIn: number };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(reducer, { idToken: null, email: null, isLoading: true });
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentSessionRef = useRef<StoredSession | null>(null);

  function clearSessionState() {
    if (refreshTimerRef.current) { clearTimeout(refreshTimerRef.current); refreshTimerRef.current = null; }
    currentSessionRef.current = null;
    sessionStorage.removeItem(TOKEN_KEY);
    dispatch({ type: 'CLEAR_SESSION' });
  }

  function applySession(session: StoredSession) {
    currentSessionRef.current = session;
    persistSession(session);
    dispatch({ type: 'SET_SESSION', session });
    scheduleRefresh(session);
  }

  function scheduleRefresh(session: StoredSession) {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    const delay = session.expiresAt - Date.now() - REFRESH_BUFFER_MS;
    if (delay <= 0) return;
    refreshTimerRef.current = setTimeout(() => { void doRefresh(); }, delay);
  }

  async function doRefresh(): Promise<void> {
    if (inFlightRefresh) return inFlightRefresh;
    inFlightRefresh = (async () => {
      try {
        const refresh = loadRefresh();
        if (!refresh) { clearSessionState(); return; }

        const now = Date.now();
        if (now > refresh.sessionStartedAt + SESSION_TTL_MS) {
          clearSessionState();
          clearRefresh();
          return;
        }

        const data = await exchangeRefresh(portalConfig.apiBase, refresh.refreshToken);
        const { email, exp } = parseIdToken(data.idToken);
        const hardCap = refresh.sessionStartedAt + SESSION_TTL_MS;
        const session: StoredSession = {
          idToken: data.idToken,
          email,
          expiresAt: Math.min(exp * 1000, hardCap),
          sessionStartedAt: refresh.sessionStartedAt,
        };
        applySession(session);
      } catch {
        clearSessionState();
      } finally {
        inFlightRefresh = null;
      }
    })();
    return inFlightRefresh;
  }

  async function signIn(): Promise<void> {
    const oauthState = randomString(24);
    sessionStorage.setItem(STATE_KEY, oauthState);

    const codeVerifier = randomString(64);
    sessionStorage.setItem(VERIFIER_KEY, codeVerifier);

    const codeChallenge = await sha256Base64Url(codeVerifier);

    const params = new URLSearchParams({
      client_id: portalConfig.googleClientId,
      response_type: 'code',
      scope: 'openid email profile',
      redirect_uri: window.location.origin + '/',
      state: oauthState,
      hd: portalConfig.allowedHostedDomains[0]!,
      access_type: 'offline',
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  }

  function signOut() {
    clearSessionState();
    clearRefresh();
  }

  useEffect(() => {
    void (async () => {
      const urlParams = new URLSearchParams(window.location.search);

      const errorState = urlParams.get('state');
      const storedState = sessionStorage.getItem(STATE_KEY);
      if (urlParams.has('error') && storedState && errorState === storedState) {
        cleanUrl();
        sessionStorage.removeItem(STATE_KEY);
        sessionStorage.removeItem(VERIFIER_KEY);
        dispatch({ type: 'LOADED' });
        return;
      }

      const code = urlParams.get('code');
      const oauthState = urlParams.get('state');
      if (code && oauthState) {
        const savedState = sessionStorage.getItem(STATE_KEY);
        const codeVerifier = sessionStorage.getItem(VERIFIER_KEY);
        sessionStorage.removeItem(STATE_KEY);
        sessionStorage.removeItem(VERIFIER_KEY);

        if (oauthState !== savedState || !codeVerifier) {
          cleanUrl();
          dispatch({ type: 'LOADED' });
          return;
        }

        try {
          const tokens = await exchangeCode(portalConfig.apiBase, code, codeVerifier);
          const { email, exp } = parseIdToken(tokens.idToken);
          const now = Date.now();

          const existingRefresh = loadRefresh();
          let sessionStartedAt: number;
          if (tokens.refreshToken) {
            sessionStartedAt = now;
            saveRefresh({ refreshToken: tokens.refreshToken, sessionStartedAt });
          } else if (existingRefresh && now <= existingRefresh.sessionStartedAt + SESSION_TTL_MS) {
            sessionStartedAt = existingRefresh.sessionStartedAt;
          } else {
            sessionStartedAt = now;
          }

          const session: StoredSession = {
            idToken: tokens.idToken,
            email,
            expiresAt: Math.min(exp * 1000, sessionStartedAt + SESSION_TTL_MS),
            sessionStartedAt,
          };
          cleanUrl();
          applySession(session);
        } catch {
          cleanUrl();
          dispatch({ type: 'LOADED' });
        }
        return;
      }

      const stored = sessionStorage.getItem(TOKEN_KEY);
      if (stored) {
        try {
          const parsed = JSON.parse(stored) as StoredSession;
          const now = Date.now();
          if (now > parsed.sessionStartedAt + SESSION_TTL_MS) {
            sessionStorage.removeItem(TOKEN_KEY);
            clearRefresh();
            dispatch({ type: 'LOADED' });
          } else if (parsed.expiresAt <= now + REFRESH_BUFFER_MS) {
            currentSessionRef.current = parsed;
            await doRefresh();
          } else {
            applySession(parsed);
          }
          return;
        } catch {
          sessionStorage.removeItem(TOKEN_KEY);
        }
      }

      const refresh = loadRefresh();
      if (refresh) {
        const now = Date.now();
        if (now > refresh.sessionStartedAt + SESSION_TTL_MS) {
          clearRefresh();
          dispatch({ type: 'LOADED' });
        } else {
          await doRefresh();
        }
        return;
      }

      dispatch({ type: 'LOADED' });
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AuthContext.Provider value={{ idToken: state.idToken, email: state.email, isLoading: state.isLoading, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  return useContext(AuthContext);
}
